import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Loader2, Send, CheckCircle2, ArrowRight, MailPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function SmartRoutingButton() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("compose"); // compose | result
  const [form, setForm] = useState({ from_email: "", from_name: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const route = async () => {
    if (!form.from_email || !form.subject || !form.body) { toast.error("Compila tutti i campi"); return; }
    setBusy(true);
    try { const { data } = await api.post("/ai/route-email", form); setResult(data); setStep("result"); }
    catch { toast.error("Errore AI"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/route-email/apply", {
        pratica_id: result.pratica_id, azioni_suggerite: result.azioni_suggerite,
        categoria: result.categoria, priorita: result.priorita,
        create_memo: true, create_task: !!result.pratica_id,
        original_email: form,
      });
      toast.success("Routing applicato: memo, task e archivio email creati");
      setOpen(false); setStep("compose"); setForm({ from_email: "", from_name: "", subject: "", body: "" }); setResult(null);
      if (data.memo_id) navigate("/memo");
    } catch { toast.error("Errore applicazione"); }
    finally { setBusy(false); }
  };

  const tryDemo = () => setForm({
    from_email: "info@rossisrl.it",
    from_name: "Mario Rossi",
    subject: "Documenti integrativi per pratica M&A Ospita",
    body: "Buongiorno,\n\nin allegato i documenti integrativi richiesti per la due diligence della pratica M&A Ospita. Si tratta del bilancio 2024 certificato, del piano industriale aggiornato e della lista clienti.\n\nRestiamo in attesa di un Vostro feedback entro venerdì prossimo, in vista del closing previsto.\n\nCordiali saluti,\nMario Rossi\nUfficio legale Rossi SRL"
  });

  return (
    <>
      <Button onClick={()=>setOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-navy-900" data-testid="smart-routing-btn">
        <Sparkles className="w-4 h-4 mr-1.5" /> Smart Routing AI
      </Button>
      <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if (!v) { setStep("compose"); setResult(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Smart Routing AI · {step === "compose" ? "Email in ingresso" : "Proposta di smistamento"}
            </DialogTitle>
          </DialogHeader>

          {step === "compose" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-600">Incolla qui un'email/PEC ricevuta. L'AI identificherà la pratica, suggerirà azioni e una bozza di risposta.</p>
                <Button variant="ghost" size="sm" onClick={tryDemo} className="text-xs" data-testid="sr-demo">Usa demo</Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Mittente</Label><Input value={form.from_email} onChange={(e)=>setForm({...form, from_email: e.target.value})} placeholder="info@cliente.it" data-testid="sr-from-email" /></div>
                <div className="space-y-1.5"><Label>Nome</Label><Input value={form.from_name} onChange={(e)=>setForm({...form, from_name: e.target.value})} placeholder="Nome mittente" /></div>
              </div>
              <div className="space-y-1.5"><Label>Oggetto</Label><Input value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} placeholder="Oggetto email" data-testid="sr-subject" /></div>
              <div className="space-y-1.5"><Label>Corpo</Label><Textarea rows={8} value={form.body} onChange={(e)=>setForm({...form, body: e.target.value})} placeholder="Incolla qui il corpo dell'email..." data-testid="sr-body" /></div>
            </div>
          )}

          {step === "result" && result && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Pratica identificata</div>
                  <div className="font-mono text-sm text-navy-900 font-semibold mt-1" data-testid="sr-pratica">{result.pratica_protocollo || "—"}</div>
                </Card>
                <Card className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Categoria</div>
                  <Badge variant="outline" className="text-xs mt-1 capitalize">{(result.categoria || "informativa").replace("_"," ")}</Badge>
                </Card>
                <Card className="p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Priorità</div>
                  <Badge className={`mt-1 ${result.priorita === "urgente" ? "bg-red-100 text-red-700" : result.priorita === "alta" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"} border-0`}>{result.priorita}</Badge>
                </Card>
              </div>

              {result.matched_contact && (
                <div className="text-xs px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded inline-flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3" /> Mittente già in rubrica: <strong>{result.matched_contact.name}</strong>
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Azioni suggerite</div>
                <ul className="space-y-1.5">
                  {(result.azioni_suggerite || []).map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-800">
                      <ArrowRight className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Bozza risposta</div>
                <Textarea rows={7} value={result.bozza_risposta || ""} readOnly className="text-sm bg-slate-50 font-serif" data-testid="sr-bozza" />
              </div>

              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded flex items-center gap-2">
                <MailPlus className="w-3 h-3" /> Applicando crei: 1 memo, {result.pratica_id ? "1 task " : ""}e archivi l'email originale.
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {step === "compose" && (
              <>
                <Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button>
                <Button className="bg-amber-500 hover:bg-amber-400 text-navy-900" onClick={route} disabled={busy} data-testid="sr-analyze">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" />Analizza con Claude</>}
                </Button>
              </>
            )}
            {step === "result" && (
              <>
                <Button variant="outline" onClick={()=>setStep("compose")}>← Modifica</Button>
                <Button onClick={apply} disabled={busy} className="bg-navy-800 hover:bg-navy-900" data-testid="sr-apply">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />Applica</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
